// src/pages/AdminLms.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  updateDoc,
  doc,
} from "firebase/firestore";
//@ts-ignore
import { db } from "../../utils/firebase.js";
//@ts-ignore
import { useAuth } from "../../state/AuthContext";

type Lead = {
  id?: string;
  leadname?: string;
  businessname?: string;
  city?: string;
  address?: string;
  mobile?: string | number;
  mobile2?: string | number;
  contactEmail?: string;
  leadvalue?: string | number;
  comments?: string;
  status?: string;
  createddate?: string;
  updateddate?: string;
  leadowner?: string;
};

const ROWS_PER_PAGE = 20;
const ZAFFLO_GREEN = "#6cbb23";

export default function AdminLms() {
  const { user, logout } = useAuth();
  const adminEmail = user?.email || localStorage.getItem("user") || "";
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState<{ success: number; failed: number; errors: string[] } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Partial<Lead>>({});
  const [unmasked, setUnmasked] = useState<Record<string, boolean>>({});
  const [waOpen, setWaOpen] = useState(false);
  const [waLead, setWaLead] = useState<Lead | null>(null);
  const [messageText, setMessageText] = useState("");
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);

  // NOTE: We do NOT fetch leads automatically on load to save reads.
  // Admin must click "Fetch Leads" button to load data.

  // UI-only search
  const handleSearch = () => {
    if (!searchTerm) {
      setFilteredLeads(leads);
      setCurrentPage(1);
      return;
    }
    const t = searchTerm.toLowerCase();
    const f = leads.filter((lead) =>
      Object.values(lead || {}).some((v) => String(v ?? "").toLowerCase().includes(t))
    );
    setFilteredLeads(f);
    setCurrentPage(1);
  };

  // fetch leads (all leads) for admin - triggered by button
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "leads"));
      const snap = await getDocs(q);
      const arr: Lead[] = [];
      snap.forEach((d) => {
        arr.push({ id: d.id, ...(d.data() as any) });
      });
      // keep order as-is
      setLeads(arr);
      setFilteredLeads(arr);
      setCurrentPage(1);
    } catch (err) {
      console.error("fetchLeads error", err);
      alert("Error fetching leads");
    } finally {
      setLoading(false);
    }
  };

  // pagination helpers
  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / ROWS_PER_PAGE));
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const pageLeads = filteredLeads.slice(startIndex, startIndex + ROWS_PER_PAGE);

  // inline edit
  const startEdit = (lead: Lead) => {
    setEditingId(lead.id || null);
    setEditValues({ ...lead });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };
  const saveEdit = async (id?: string) => {
    if (!id) return;
    try {
      const payload: any = {
        ...editValues,
        updateddate: new Date().toISOString(),
      };
      // avoid overwriting createddate if not present in editValues
      if ((payload as any).createddate === undefined) delete (payload as any).createddate;
      await updateDoc(doc(db, "leads", id), payload as any);
      // update UI
      setLeads((prev) => prev.map((p) => (p.id === id ? { ...p, ...(payload as any) } : p)));
      setFilteredLeads((prev) => prev.map((p) => (p.id === id ? { ...p, ...(payload as any) } : p)));
      setEditingId(null);
      setEditValues({});
      alert("Saved");
    } catch (err) {
      console.error("saveEdit err", err);
      alert("Error saving");
    }
  };

  // mask/unmask
  const maskPhone = (num?: string | number) => {
    if (!num) return "";
    const s = String(num);
    if (s.length <= 4) return "*".repeat(s.length);
    const first = s.slice(0, 2);
    const last = s.slice(-2);
    const middle = "*".repeat(Math.max(0, s.length - 4));
    return `${first}${middle}${last}`;
  };
  const toggleUnmask = (id?: string) => {
    if (!id) return;
    setUnmasked((p) => ({ ...p, [id]: !p[id] }));
  };

  // WhatsApp modal handlers
  const openWhatsapp = (lead: Lead) => {
    setWaLead(lead);
    setMessageText(`Hi ${lead.leadname || lead.businessname || ""}, `);
    setSelectedMessage(null);
    setWaOpen(true);
  };
  const closeWhatsapp = () => {
    setWaOpen(false);
    setWaLead(null);
    setMessageText("");
  };
  const applyTemplate = (tpl: string) => {
    if (!waLead) return;
    const emp = user?.displayName || adminEmail.split("@")[0];
    const final = tpl.replace(/\[Name\]/g, waLead.leadname || waLead.businessname || "")
                     .replace(/\[Your Name\]/g, emp);
    setMessageText(final);
    setSelectedMessage(tpl);
  };
  const sendWhatsapp = () => {
    if (!waLead) return;
    const phone = String(waLead.mobile || "").replace(/\D/g, "");
    if (!phone) {
      alert("No phone number");
      return;
    }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(messageText)}`;
    window.open(url, "_blank");
    closeWhatsapp();
  };

  // CSV template download (now includes leadowner)
  const downloadTemplate = () => {
    // headers: leadname,businessname,city,address,mobile,mobile2,contactEmail,leadvalue,comments,leadowner
    const headers = ["leadname","businessname","city","address","mobile","mobile2","contactEmail","leadvalue","comments","leadowner"];
    const csv = headers.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  // CSV parsing + upload (accepts CSV with leadowner)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Only accept text/csv mime or .csv extension
    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Please upload a CSV file (Excel can save as CSV).");
      return;
    }
    setUploading(true);
    setUploadReport(null);
    const text = await file.text();
    // split lines
    const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
    if (lines.length < 2) {
      alert("CSV appears empty. Make sure it has header row and at least one data row.");
      setUploading(false);
      return;
    }
    const headerRow = lines[0].split(",").map((h) => h.trim());
    // expected headers (now includes leadowner)
    const allowed = ["leadname","businessname","city","address","mobile","mobile2","contactEmail","leadvalue","comments","leadowner"];
    // Map header positions
    const headerMap: Record<string, number> = {};
    headerRow.forEach((h, i) => { headerMap[h] = i; });
    // Validate presence of required headers
    const missing = allowed.filter((h) => !headerMap.hasOwnProperty(h));
    if (missing.length > 0) {
      alert("Missing headers in CSV: " + missing.join(", "));
      setUploading(false);
      return;
    }

    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    // process each data line
    for (let i = 1; i < lines.length; i++) {
      const row = lines[i];
      if (!row.trim()) continue;
      // naive CSV split by commas — this will fail with commas inside fields. For robust parsing use a CSV library.
      const cols = row.split(",").map((c) => c.trim());
      const lead: any = {};
      allowed.forEach((h) => {
        const idx = headerMap[h];
        lead[h] = idx !== undefined ? cols[idx] ?? "" : "";
      });

      // validations
      const mobile = String(lead.mobile || "");
      const mobileRegex = /^\d{12}$/;
      if (!mobileRegex.test(mobile)) {
        failed++;
        errors.push(`Row ${i+1}: invalid mobile (${mobile})`);
        continue;
      }

      // leadowner presence: if provided use it, else fallback to adminEmail
      const leadowner = String(lead.leadowner || "").trim() || adminEmail;

      // create payload
      const now = new Date().toISOString();
      const payload: any = {
        leadname: lead.leadname || "",
        businessname: lead.businessname || "",
        city: lead.city || "",
        address: lead.address || "",
        mobile: mobile,
        mobile2: lead.mobile2 || "",
        contactEmail: lead.contactEmail || "",
        leadvalue: lead.leadvalue || "",
        comments: lead.comments || "",
        leadowner: leadowner,
        status: "New",
        createddate: now,
        updateddate: now,
      };

      try {
        await addDoc(collection(db, "leads"), payload);
        success++;
      } catch (err) {
        console.error("upload addDoc err", err);
        failed++;
        errors.push(`Row ${i+1}: firebase error`);
      }
    }

    setUploadReport({ success, failed, errors });
    setUploading(false);
    if (failed === 0) {
      alert(`Upload complete — ${success} records added.`);
    } else {
      alert(`Upload complete — ${success} success, ${failed} failed. Check console or report.`);
    }
    // refresh if admin wants (do not auto-fetch unless they clicked fetch)
  };

  // small UI helpers - templates
  const TEMPLATES = useMemo(() => [
    { title: "Intro: Quick Demo", text: "Hi [Name], I’m [Your Name] from Zafflo. We help hotels digitize room service & restaurant orders. Guests scan a QR, order from phone, and your staff gets orders on WhatsApp. Free setup + 1 year free. Can I share a quick demo?" },
    { title: "Follow-up: Features", text: "Hi [Name], Zafflo reduces reception calls, enables QR ordering, and provides analytics. Start free for 1 year. Shall I set up a free trial?" },
    { title: "Post-demo Check", text: "Hi [Name], did you get a chance to try the demo? Hotels using Zafflo see up to 30% more in-room orders. Want help to get started?" },
    { title: "Exit / Link", text: "Whenever you're ready, here’s the link 👉 https://zafflo.com. Have a great day!" }
  ], []);

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Admin — Lead Management</h1>
          <p className="text-sm text-gray-500">Bulk upload leads & manage all leads</p>
        </div>
        <div>
          <button
            onClick={() => { logout?.(() => {}); }}
            className="px-3 py-2 rounded border"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Upload area */}
      <div className="bg-white p-4 rounded shadow border-l-4 border-[#6cbb23]">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div>
            <button onClick={downloadTemplate} className="px-4 py-2 bg-[#6cbb23] text-white rounded">Download CSV Template</button>
          </div>

          <div className="flex flex-col">
            <label className="block text-sm mb-1">Upload CSV (Excel → Save as CSV) — include <span className="font-medium">leadowner</span> column (owner email)</label>
            <input type="file" accept=".csv" onChange={handleFileUpload} className="border p-2 rounded" />
            {uploading && <div className="text-sm text-gray-600 mt-2">Uploading...</div>}
            {uploadReport && (
              <div className="mt-2 text-sm">
                <div>Success: {uploadReport.success}</div>
                <div>Failed: {uploadReport.failed}</div>
                {uploadReport.errors.length > 0 && (
                  <div className="text-xs text-red-600 mt-1">Errors: {uploadReport.errors.slice(0,3).join("; ")}</div>
                )}
              </div>
            )}
          </div>

          <div className="ml-auto">
            <button onClick={fetchLeads} className="px-4 py-2 bg-blue-600 text-white rounded">Fetch Leads</button>
          </div>
        </div>
      </div>

      {/* Table toolbar */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="flex items-center gap-2">
          <input value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} placeholder="Search leads..." className="border px-3 py-2 rounded w-64" onKeyDown={(e) => e.key === "Enter" && handleSearch()} />
          <button onClick={handleSearch} className="px-3 py-2 bg-[#6cbb23] text-white rounded">Search</button>
        </div>
        <div className="text-sm text-gray-600">Showing {filteredLeads.length} leads</div>
      </div>

      {/* Leads table */}
      <div className="bg-white rounded shadow overflow-auto">
        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left">Lead</th>
                <th className="px-3 py-2 text-left">Business</th>
                <th className="px-3 py-2 text-left">City</th>
                <th className="px-3 py-2 text-left">Mobile</th>
                <th className="px-3 py-2 text-left">Owner (email)</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Value</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Updated</th>
                <th className="px-3 py-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageLeads.map((lead) => (
                <tr key={lead.id} className={`${lead?.status === "New" ? "bg-blue-50" : ""}`}>
                  <td className="px-3 py-2">
                    {editingId === lead.id ? (
                      <input value={editValues.leadname || ""} onChange={(e)=>setEditValues(s=>({...s, leadname: e.target.value}))} className="border px-2 py-1 rounded w-40" />
                    ) : (
                      lead.leadname
                    )}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === lead.id ? (
                      <input value={editValues.businessname || ""} onChange={(e)=>setEditValues(s=>({...s, businessname: e.target.value}))} className="border px-2 py-1 rounded" />
                    ) : (
                      lead.businessname
                    )}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === lead.id ? (
                      <input value={editValues.city || ""} onChange={(e)=>setEditValues(s=>({...s, city: e.target.value}))} className="border px-2 py-1 rounded" />
                    ) : (
                      lead.city
                    )}
                  </td>

                  <td className="px-3 py-2 flex items-center gap-2">
                    <span className="font-mono">{unmasked[lead.id || ""] ? lead.mobile : maskPhone(lead.mobile)}</span>
                    <button onClick={()=>toggleUnmask(lead.id)} className="p-1 rounded hover:bg-gray-100" title={unmasked[lead.id || ""] ? "Hide" : "View"}>
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </td>

                  <td className="px-3 py-2">
                    {/* allow admin to edit leadowner inline */}
                    {editingId === lead.id ? (
                      <input value={editValues.leadowner || ""} onChange={(e)=>setEditValues(s=>({...s, leadowner: e.target.value}))} className="border px-2 py-1 rounded w-56" />
                    ) : (
                      <span className="text-xs text-gray-700">{lead.leadowner || "—"}</span>
                    )}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === lead.id ? (
                      <select value={editValues.status || lead.status} onChange={(e)=>setEditValues(s=>({...s, status: e.target.value}))} className="border px-2 py-1 rounded">
                        {["New","Contacted","Interested","Contact Later","Won Close","Lost Dead"].map(s=> <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <span className="px-2 py-1 rounded text-sm">{lead.status}</span>
                    )}
                  </td>

                  <td className="px-3 py-2">
                    {editingId === lead.id ? (
                      <input value={editValues.leadvalue || ""} onChange={(e)=>setEditValues(s=>({...s, leadvalue: e.target.value}))} className="border px-2 py-1 rounded w-24" />
                    ) : (
                      lead.leadvalue
                    )}
                  </td>

                  <td className="px-3 py-2 text-xs text-gray-500">{lead.createddate || "—"}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{lead.updateddate || "—"}</td>
                  <td className="px-3 py-2">
                    {editingId === lead.id ? (
                      <div className="flex gap-2">
                        <button onClick={()=>saveEdit(lead.id)} className="px-2 py-1 rounded border text-[#6cbb23]">Save</button>
                        <button onClick={cancelEdit} className="px-2 py-1 rounded border">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <button onClick={()=>startEdit(lead)} className="px-2 py-1 rounded border">Edit</button>
                        <button onClick={()=>openWhatsapp(lead)} className="px-2 py-1 rounded bg-[#25D366] text-white">WhatsApp</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* pagination */}
      <div className="flex justify-end items-center gap-3">
        <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1} className="px-3 py-1 border rounded">Prev</button>
        <div className="px-3 py-1 border rounded bg-gray-50">{currentPage} / {totalPages}</div>
        <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages} className="px-3 py-1 border rounded">Next</button>
      </div>

      {/* WhatsApp modal */}
      {waOpen && waLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="w-full max-w-3xl bg-white rounded shadow-lg overflow-hidden">
            <div className="flex">
              {/* left: templates */}
              <div className="w-1/3 bg-[#E9FCEB] p-3 border-r">
                <div className="p-2 bg-[#25D366] text-white rounded mb-3 font-semibold">💬 Templates</div>
                <div className="space-y-2 overflow-y-auto max-h-[420px]">
                  {TEMPLATES.map((t, i) => (
                    <div key={i} onClick={()=>applyTemplate(t.text)} className={`p-2 rounded cursor-pointer ${selectedMessage === t.text ? "bg-[#DCF8C6]" : "bg-white"} hover:bg-[#DFFFE2] border`}>
                      <div className="font-medium text-sm">{t.title}</div>
                      <div className="text-xs text-gray-600">{t.text.length > 80 ? t.text.slice(0,80) + "..." : t.text}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* right: message and send */}
              <div className="flex-1 p-4 flex flex-col">
                <div className="flex justify-between items-center mb-2">
                  <div className="font-semibold">To: {waLead.leadname || waLead.businessname}</div>
                  <div className="text-xs text-gray-500">Number: {waLead.mobile}</div>
                </div>

                <div className="flex-1 border rounded p-3 bg-gray-50 overflow-auto">
                  <textarea value={messageText} onChange={(e)=>setMessageText(e.target.value)} rows={10} className="w-full h-full bg-transparent resize-none outline-none"></textarea>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={closeWhatsapp} className="px-4 py-2 rounded border">Cancel</button>
                  <button onClick={sendWhatsapp} className="px-4 py-2 rounded bg-[#25D366] text-white">Send on WhatsApp</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
